"use client";

import { createContext, useContext, type ReactNode } from "react";
import type {
  ViewType,
  Event,
  TimeRange,
  BookedTimeRange,
} from "@/lib/calendar-types";

interface CalendarContextType {
  view: ViewType;
  currentDate: Date;
  events: Event[];
  selectedRanges: TimeRange[];
  bookedRanges: BookedTimeRange[];
  selectionMode?: "click-only" | "drag";
  enforceRangeRestriction?: boolean;
  onViewChange: (view: ViewType) => void;
  onDateChange: (date: Date) => void;
  onEditEvent: (event: Event) => void;
  onAddTimeRange: (range: TimeRange) => void;
  onRemoveTimeRange: (rangeId: string) => void;
  onClearAllSelections: () => void;
  showConflictToast: (message: string) => void;
  onSlotClick?: (slot: Date) => void;
}

const CalendarContext = createContext<CalendarContextType | undefined>(
  undefined,
);

export function CalendarProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: CalendarContextType;
}) {
  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}

export function useCalendar() {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error("useCalendar must be used within a CalendarProvider");
  }
  return context;
}
